'use client';

import { useEffect, useRef, useState } from 'react';
import { pollBackoffDelay } from '@/lib/utils/poll-backoff';

const POLL_INTERVAL_MS = 5000;
// 60 attempts × 5s = 5 min at the healthy cadence. Generation typically lands
// in <30s; the cap protects against burning cycles on variants that never get
// an image (failed jobs, variant_not_feasible — though the latter is filtered
// upstream). Under failure backoff the same attempt budget stretches over a
// longer wall-clock window — intended (fewer requests against a struggling
// database, same bound on total requests) but bounded by the wall-clock cap
// below, so the budget can never stretch it into hours.
const MAX_POLL_ATTEMPTS = 60;
// Absolute wall-clock bound on a single poll run (#754 follow-up). With the
// failure backoff at its 120s ceiling, the attempt budget alone would let the
// pending treatment linger for ~2 hours during a sustained outage — an
// "generating…" overlay that stale stops being honest. Ten minutes is double
// the healthy-budget window: plenty for a slow job, well short of absurd.
const MAX_POLL_WALL_CLOCK_MS = 10 * 60_000;

/**
 * One regeneration run.
 *
 * `baseline` is the url that was on screen when the run was queued: a
 * regeneration replaces an image that is ALREADY there, so "the job finished"
 * cannot be read off the url merely being present — it has to be a DIFFERENT
 * url. Stored urls carry a ?v= cache-buster, so a re-shot photo always is one.
 *
 * Identity is the object itself — a fresh one per run — so a second
 * regeneration re-arms the poll rather than reusing the settled first.
 */
interface Flight {
  baseline: string | null;
}

export interface UseGeneratedImageOptions {
  /**
   * True while an image job for THIS recipe + variant is known to be in flight.
   *
   * The hook used to infer "still generating" from `imageUrl` being empty,
   * which is only true for a FIRST image — on a regeneration the recipe already
   * has one, so it never polled and the new photo never appeared without a
   * manual refresh (#751). The caller that queued the job is the one thing that
   * knows a run started; it passes that in rather than the hook guessing.
   */
  generationInFlight?: boolean;
  /**
   * Called once per flight when it ends — the new url, or null when the poll
   * gave up (attempt cap, or a 404 saying the recipe is gone). Lets the caller
   * that raised a "regenerating…" toast resolve it without running a second
   * poll loop of its own against the same endpoint.
   */
  onResolved?: (imageUrl: string | null) => void;
}

export interface GeneratedImageState {
  /** Resolved URL, or null while the image is still being generated. */
  imageUrl: string | null;
  /** True while a poll is running — i.e. the pending treatment should show.
   *  During a regeneration `imageUrl` is non-null AND this is true: the old
   *  photo stays on screen under a pending overlay. */
  isPending: boolean;
  /** True only for an image that landed mid-session, so the consumer can
   *  cross-fade it in. False on SSR-rendered images, which must not animate. */
  justArrived: boolean;
}

/**
 * Poll for a recipe image that a background job is still generating.
 *
 * Extracted from RecipeImage so the hero and the thumbnail share ONE polling
 * implementation (#646). They render the wait very differently — full-bleed
 * with a caption vs a 64px square — but "has the image landed yet" is the same
 * question, and two copies of it would drift on interval, cap and stop
 * conditions.
 *
 * Two things start a poll:
 *   - there is no image yet (a first generation, the original case), or
 *   - `options.generationInFlight` says a run was queued for an image that
 *     already exists (a regeneration, #751).
 *
 * Both are bounded by the same MAX_POLL_ATTEMPTS cap, and both stop on a 404
 * (recipe gone or not visible). A 503 — which is what the status route answers
 * when the DATABASE is what failed — is transient and keeps polling until the
 * cap; it must never be conflated with the terminal 404 (CLAUDE.md §20).
 */
export function useGeneratedImage(
  initialImageUrl: string | null | undefined,
  recipeId?: string,
  variantType?: string | null,
  options?: UseGeneratedImageOptions,
): GeneratedImageState {
  const generationInFlight = options?.generationInFlight ?? false;

  const [imageUrl, setImageUrl] = useState<string | null>(initialImageUrl ?? null);
  const justArrivedRef = useRef(false);

  // The callback is a fresh closure on every parent render. Held in a ref so it
  // stays out of the effect's deps — depending on it would tear down and
  // restart the poll (resetting the attempt count) on any parent re-render.
  const onResolvedRef = useRef(options?.onResolved);
  // A resolution decided during RENDER (the re-seed path below) can't call the
  // parent back on the spot — that would be a setState on another component
  // mid-render. Park it and flush after commit.
  const pendingResolveRef = useRef<{ url: string | null } | null>(null);
  useEffect(() => {
    onResolvedRef.current = options?.onResolved;
    const parked = pendingResolveRef.current;
    if (parked) {
      pendingResolveRef.current = null;
      onResolvedRef.current?.(parked.url);
    }
  });

  const [flight, setFlight] = useState<Flight | null>(null);
  const [settledFlight, setSettledFlight] = useState<Flight | null>(null);

  // `imageUrl` is seeded from the prop and then owned locally (the poller writes
  // to it), so a parent that resolves a NEW url — a regeneration landing, a
  // server refresh — would otherwise be ignored for the life of the mount.
  // Re-seed whenever the incoming prop actually changes. Stored URLs carry a
  // ?v= cache-buster, so a re-shot photo is always a different string.
  const [seededFrom, setSeededFrom] = useState<string | null>(initialImageUrl ?? null);
  if ((initialImageUrl ?? null) !== seededFrom) {
    setSeededFrom(initialImageUrl ?? null);
    setImageUrl(initialImageUrl ?? null);
    // A different url arriving from the parent DURING a flight is the
    // regenerated photo reaching us by another route (a router.refresh(), a
    // navigation) before our own poll saw it. Same event, so it animates and
    // ends the flight. Outside a flight this is an ordinary prop change — the
    // first render included — and must not animate.
    // `settledFlight !== flight` keeps this from firing a SECOND resolution for
    // a run our own poll already resolved: the parent typically answers that
    // resolution by passing the new url down, which lands right here.
    if (
      flight &&
      settledFlight !== flight &&
      initialImageUrl &&
      initialImageUrl !== flight.baseline
    ) {
      justArrivedRef.current = true;
      setSettledFlight(flight);
      pendingResolveRef.current = { url: initialImageUrl };
    }
  }

  // Flight bookkeeping, derived from the caller's flag. Done in render so the
  // very first poll of a run goes out immediately rather than one tick late.
  if (generationInFlight && flight === null) {
    setFlight({ baseline: imageUrl });
  } else if (!generationInFlight && flight !== null) {
    setFlight(null);
    setSettledFlight(null);
  }

  const awaitingRegeneration = flight !== null && settledFlight !== flight;
  const canPoll = Boolean(recipeId) && (awaitingRegeneration || !imageUrl);

  useEffect(() => {
    if (!canPoll || !recipeId) return;

    let attempts = 0;
    let cancelled = false;
    // Outside a flight there is nothing on screen, so ANY url is the arrival —
    // which is the same test with a null baseline.
    const baseline = flight ? flight.baseline : null;

    // Ends the current run, exactly once. Only a regeneration has a caller
    // waiting on the outcome; a first generation has nobody to tell.
    const settle = (url: string | null) => {
      if (!flight) return;
      setSettledFlight(flight);
      onResolvedRef.current?.(url);
    };

    // Chained setTimeout, NOT setInterval (#754). setInterval keeps firing on
    // schedule whether or not the previous tick has resolved, and this fetch
    // can take connect_timeout (10 s) + retries when the database is slow — so
    // during exactly the outage that makes it slow, ticks overlapped and the
    // poller stacked requests on itself, each landing on a fresh serverless
    // instance holding pooler slots. Scheduling the next tick only after the
    // current one settles caps this poller at ONE request in flight, and the
    // failure backoff stretches the cadence instead of hammering the resource
    // that just refused us (same pattern as the notification bell, #711).
    let timer: ReturnType<typeof setTimeout> | null = null;
    let consecutiveFailures = 0;
    const startedAt = Date.now();

    const scheduleNext = () => {
      if (cancelled) return;
      // Shared shape (src/lib/utils/poll-backoff.ts): plain cadence while
      // healthy, jittered exponential stretch under failure so parallel mounts
      // (hero + thumbnail, several tabs) drift apart instead of marching back
      // onto the recovering database in step.
      timer = setTimeout(tick, pollBackoffDelay(POLL_INTERVAL_MS, consecutiveFailures));
    };

    const tick = async () => {
      attempts += 1;
      try {
        const qs = variantType ? `?variant=${encodeURIComponent(variantType)}` : '';
        const res = await fetch(`/api/recipes/${recipeId}/image-status${qs}`, {
          cache: 'no-store',
        });
        if (!res.ok) {
          // 404 → recipe gone or not visible; stop polling. Every other
          // non-OK status (503 when the database is unreachable) is transient
          // but backs the cadence off.
          if (res.status === 404) {
            cancelled = true;
            settle(null);
            return;
          }
          consecutiveFailures += 1;
          return;
        }
        consecutiveFailures = 0;
        const data: { imageUrl: string | null } = await res.json();
        // During a flight the old url is still what the endpoint returns until
        // the job writes the new one — only a CHANGED url means "landed".
        if (data.imageUrl && data.imageUrl !== baseline && !cancelled) {
          justArrivedRef.current = true;
          setImageUrl(data.imageUrl);
          cancelled = true;
          settle(data.imageUrl);
        }
      } catch {
        // Network blip — keep polling until cap, but back off.
        consecutiveFailures += 1;
      } finally {
        if (!cancelled) {
          // Two bounds, either ends the run as "gave up" (settle(null) → the
          // caller's delayed-toast path): the attempt budget, and the absolute
          // wall clock — backoff must never stretch the pending treatment
          // into hours.
          if (
            attempts >= MAX_POLL_ATTEMPTS ||
            Date.now() - startedAt >= MAX_POLL_WALL_CLOCK_MS
          ) {
            cancelled = true;
            settle(null);
          } else {
            scheduleNext();
          }
        }
      }
    };

    // Kick off the first poll right away so users with fast-completing jobs
    // don't wait a full 5s before the image swap.
    tick();

    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [canPoll, recipeId, variantType, flight]);

  return { imageUrl, isPending: canPoll, justArrived: justArrivedRef.current };
}
